import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, LayoutDashboard, Wallet, PieChart, TrendingUp, Briefcase,
  Trash2, Star, Pencil, Check, X, Loader2,
  UtensilsCrossed, Store, User, ShoppingCart, Building2
} from 'lucide-react'
import api from '../services/api'
import { useLanguage } from '../hooks/useLanguage'

interface DashboardItem {
  id: string
  name: string
  template: string | null
  widget_config: unknown[]
  is_default: number
  sort_order: number
  created_at: string
}

const TEMPLATES = [
  {
    id: 'tesoreria',
    name: 'Tesoreria',
    nameEn: 'Treasury',
    icon: Wallet,
    descIt: 'Cash flow, KPI, trend mensile e insights AI',
    descEn: 'Cash flow, KPIs, monthly trends and AI insights',
    color: 'text-blue-500 bg-blue-50 dark:bg-blue-900/30',
  },
  {
    id: 'controllo-costi',
    name: 'Controllo Costi',
    nameEn: 'Cost Control',
    icon: PieChart,
    descIt: 'Spese per categoria, costi fissi vs variabili',
    descEn: 'Expenses by category, fixed vs variable costs',
    color: 'text-amber-500 bg-amber-50 dark:bg-amber-900/30',
  },
  {
    id: 'previsionale',
    name: 'Previsionale',
    nameEn: 'Forecasting',
    icon: TrendingUp,
    descIt: 'Trend, affidabilità previsioni, scadenze fiscali',
    descEn: 'Trends, forecast reliability, fiscal deadlines',
    color: 'text-green-500 bg-green-50 dark:bg-green-900/30',
  },
  {
    id: 'executive',
    name: 'Executive',
    nameEn: 'Executive',
    icon: Briefcase,
    descIt: 'Vista compatta: KPI essenziali, cash flow, insights',
    descEn: 'Compact view: essential KPIs, cash flow, insights',
    color: 'text-purple-500 bg-purple-50 dark:bg-purple-900/30',
  },
]

const SECTOR_TEMPLATES = [
  {
    id: 'ristorazione',
    name: 'Ristorazione',
    nameEn: 'Restaurant',
    icon: UtensilsCrossed,
    descIt: 'KPI, cash flow, costi fissi/variabili, scadenze',
    descEn: 'KPIs, cash flow, fixed/variable costs, deadlines',
    color: 'text-rose-500 bg-rose-50 dark:bg-rose-900/30',
  },
  {
    id: 'retail',
    name: 'Retail / Negozio',
    nameEn: 'Retail / Store',
    icon: Store,
    descIt: 'Vendite, trend, spese, scadenze magazzino',
    descEn: 'Sales, trends, expenses, inventory deadlines',
    color: 'text-cyan-500 bg-cyan-50 dark:bg-cyan-900/30',
  },
  {
    id: 'freelance',
    name: 'Professionista',
    nameEn: 'Freelance',
    icon: User,
    descIt: 'Fatturato, scadenze fiscali, trend, insights AI',
    descEn: 'Revenue, fiscal deadlines, trends, AI insights',
    color: 'text-indigo-500 bg-indigo-50 dark:bg-indigo-900/30',
  },
  {
    id: 'ecommerce',
    name: 'E-commerce',
    nameEn: 'E-commerce',
    icon: ShoppingCart,
    descIt: 'Vendite, margini, trend, costi fissi/variabili',
    descEn: 'Sales, margins, trends, fixed/variable costs',
    color: 'text-orange-500 bg-orange-50 dark:bg-orange-900/30',
  },
  {
    id: 'immobiliare',
    name: 'Immobiliare',
    nameEn: 'Real Estate',
    icon: Building2,
    descIt: 'Cash flow, scadenze, costi fissi, trend affitti',
    descEn: 'Cash flow, deadlines, fixed costs, rental trends',
    color: 'text-teal-500 bg-teal-50 dark:bg-teal-900/30',
  },
]

export default function DashboardSelector({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { language } = useLanguage()
  const isEn = language === 'en'
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [showTemplates, setShowTemplates] = useState(false)

  const { data, isLoading } = useQuery<{ dashboards: DashboardItem[] }>({
    queryKey: ['dashboards-list'],
    queryFn: () => api.get('/dashboards').then(r => r.data),
  })

  const [createError, setCreateError] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: (payload: { name?: string; template?: string }) =>
      api.post('/dashboards', payload).then(r => r.data),
    onSuccess: (newDash) => {
      queryClient.invalidateQueries({ queryKey: ['dashboards-list'] })
      navigate(`/dashboards/${newDash.id}`)
      onClose()
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Errore nella creazione'
      setCreateError(msg)
      setTimeout(() => setCreateError(null), 4000)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/dashboards/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dashboards-list'] }),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.put(`/dashboards/${id}`, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards-list'] })
      setEditingId(null)
    },
  })

  const dashboards = data?.dashboards || []

  const startEdit = (d: DashboardItem) => {
    setEditingId(d.id)
    setEditName(d.name)
  }

  const confirmEdit = () => {
    if (editingId && editName.trim()) {
      updateMutation.mutate({ id: editingId, name: editName.trim() })
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-slate-700">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <LayoutDashboard className="w-5 h-5 text-orange-500" />
            {isEn ? 'Your Dashboards' : 'Le tue Dashboard'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto max-h-[calc(80vh-140px)]">
          {/* Existing dashboards */}
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
            </div>
          ) : dashboards.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              {isEn ? 'No custom dashboards yet. Create one or use the default.' : 'Nessuna dashboard personalizzata. Creane una o usa quella predefinita.'}
            </p>
          ) : (
            <div className="space-y-2 mb-4">
              {dashboards.map((d) => (
                <div
                  key={d.id}
                  className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 dark:border-slate-700 hover:border-orange-200 dark:hover:border-orange-800 hover:bg-orange-50/50 dark:hover:bg-orange-900/10 transition-all group"
                >
                  {/* Template icon */}
                  {(() => {
                    const tpl = TEMPLATES.find(t => t.id === d.template) || SECTOR_TEMPLATES.find(t => t.id === d.template)
                    const Icon = tpl?.icon || LayoutDashboard
                    return <Icon className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  })()}

                  {/* Name / edit */}
                  <div className="flex-1 min-w-0">
                    {editingId === d.id ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="text-sm font-medium bg-transparent border-b border-orange-300 dark:border-orange-600 outline-none px-0.5 py-0 w-full text-gray-800 dark:text-white"
                          autoFocus
                          onKeyDown={(e) => e.key === 'Enter' && confirmEdit()}
                        />
                        <button onClick={confirmEdit} className="p-1 rounded hover:bg-green-100 dark:hover:bg-green-900/30">
                          <Check className="w-3.5 h-3.5 text-green-500" />
                        </button>
                        <button onClick={() => setEditingId(null)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700">
                          <X className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { navigate(`/dashboards/${d.id}`); onClose() }}
                        className="text-sm font-medium text-gray-800 dark:text-white truncate block text-left w-full"
                      >
                        {d.name}
                      </button>
                    )}
                    {d.template && (
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {isEn
                          ? (TEMPLATES.find(t => t.id === d.template)?.nameEn || SECTOR_TEMPLATES.find(t => t.id === d.template)?.nameEn)
                          : (TEMPLATES.find(t => t.id === d.template)?.name || SECTOR_TEMPLATES.find(t => t.id === d.template)?.name)
                        }
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {d.is_default === 1 && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />}
                    <button onClick={() => startEdit(d)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700">
                      <Pencil className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                    <button
                      onClick={() => deleteMutation.mutate(d.id)}
                      className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Divider + Create */}
          <div className="border-t border-gray-100 dark:border-slate-700 pt-4">
            {!showTemplates ? (
              <button
                onClick={() => setShowTemplates(true)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-gray-200 dark:border-slate-600 text-sm font-medium text-gray-500 dark:text-gray-400 hover:border-orange-300 hover:text-orange-500 dark:hover:border-orange-600 dark:hover:text-orange-400 transition-all"
              >
                <Plus className="w-4 h-4" />
                {isEn ? 'Create Dashboard' : 'Crea Dashboard'}
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase text-gray-400 dark:text-gray-500 tracking-wider">
                  {isEn ? 'Choose a template' : 'Scegli un template'}
                </p>
                {createError && (
                  <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-600 dark:text-red-400">
                    {createError}
                  </div>
                )}

                {/* General templates */}
                <div className="grid grid-cols-2 gap-2">
                  {TEMPLATES.map((tpl) => {
                    const Icon = tpl.icon
                    return (
                      <button
                        key={tpl.id}
                        onClick={() => createMutation.mutate({ template: tpl.id })}
                        disabled={createMutation.isPending}
                        className="flex flex-col items-start gap-2 p-3 rounded-xl border border-gray-100 dark:border-slate-700 hover:border-orange-200 dark:hover:border-orange-700 hover:shadow-md transition-all text-left"
                      >
                        <div className={`p-2 rounded-lg ${tpl.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800 dark:text-white">
                            {isEn ? tpl.nameEn : tpl.name}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            {isEn ? tpl.descEn : tpl.descIt}
                          </p>
                        </div>
                      </button>
                    )
                  })}
                </div>

                {/* Sector templates */}
                <p className="text-xs font-semibold uppercase text-gray-400 dark:text-gray-500 tracking-wider mt-4">
                  {isEn ? 'By sector' : 'Per settore'}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {SECTOR_TEMPLATES.map((tpl) => {
                    const Icon = tpl.icon
                    return (
                      <button
                        key={tpl.id}
                        onClick={() => createMutation.mutate({ template: tpl.id })}
                        disabled={createMutation.isPending}
                        className="flex flex-col items-start gap-2 p-3 rounded-xl border border-gray-100 dark:border-slate-700 hover:border-orange-200 dark:hover:border-orange-700 hover:shadow-md transition-all text-left"
                      >
                        <div className={`p-2 rounded-lg ${tpl.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800 dark:text-white">
                            {isEn ? tpl.nameEn : tpl.name}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            {isEn ? tpl.descEn : tpl.descIt}
                          </p>
                        </div>
                      </button>
                    )
                  })}
                </div>

                {/* Custom empty dashboard */}
                <button
                  onClick={() => createMutation.mutate({ name: isEn ? 'Custom Dashboard' : 'Dashboard Personalizzata' })}
                  disabled={createMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-sm text-gray-500 dark:text-gray-400 hover:border-orange-300 hover:text-orange-500 dark:hover:border-orange-600 transition-all"
                >
                  {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {isEn ? 'Blank Dashboard' : 'Dashboard Vuota'}
                </button>

                <button
                  onClick={() => setShowTemplates(false)}
                  className="w-full text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 py-1"
                >
                  {isEn ? 'Cancel' : 'Annulla'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer — go to default */}
        <div className="p-4 border-t border-gray-100 dark:border-slate-700">
          <button
            onClick={() => { navigate('/'); onClose() }}
            className="w-full text-sm text-center text-orange-500 hover:text-orange-600 dark:text-orange-400 font-medium"
          >
            {isEn ? '← Back to Default Dashboard' : '← Torna alla Dashboard Predefinita'}
          </button>
        </div>
      </div>
    </div>
  )
}
